import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * SolVault product page: gallery, bundle offers with a colour per unit, and an add-to-cart that
 * goes through Horizon's cart events so the drawer, the cart icon and the cart page all update.
 */
class SvProduct extends HTMLElement {
  /** @type {number} */
  #current = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeydown);
    this.#syncPrice();
    this.#observeAtc();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#observer?.disconnect();
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return Array.from(this.querySelectorAll('[data-slide]'));
  }

  /** @returns {HTMLElement[]} */
  get #thumbs() {
    return Array.from(this.querySelectorAll('[data-thumb]'));
  }

  /** @returns {HTMLElement[]} */
  get #offers() {
    return Array.from(this.querySelectorAll('[data-offer]'));
  }

  /** @returns {HTMLElement | undefined} */
  get #selectedOffer() {
    return this.#offers.find((o) => o.classList.contains('is-sel')) ?? this.#offers[0];
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const thumb = target.closest('[data-thumb]');
    if (thumb) return this.#showSlide(Number(thumb.getAttribute('data-thumb')));

    const step = target.closest('[data-step]');
    if (step) return this.#showSlide(this.#current + Number(step.getAttribute('data-step')));

    const color = /** @type {HTMLButtonElement | null} */ (target.closest('.svp-color'));
    if (color) {
      event.stopPropagation();
      if (!color.disabled) this.#selectColor(color);
      return;
    }

    if (target.closest('.svp-offer__extra')) return;

    const offer = /** @type {HTMLElement | null} */ (target.closest('[data-offer]'));
    if (offer) return this.#selectOffer(offer);

    if (target.closest('[data-atc]')) return this.#addToCart();
    if (target.closest('[data-sticky-atc]')) return this.#scrollToBundles();

    const specs = target.closest('[data-open-specs]');
    if (specs) {
      const id = specs.getAttribute('href')?.slice(1);
      const details = id ? document.getElementById(id) : null;
      if (details instanceof HTMLDetailsElement) details.open = true;
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches('[data-size]')) {
      target.classList.remove('is-invalid');
      const errorEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
      if (errorEl) errorEl.hidden = true;
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const offer = /** @type {HTMLElement} */ (event.target).closest?.('[data-offer]');
    if (!(offer instanceof HTMLElement) || event.target !== offer) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#selectOffer(offer);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const offers = this.#offers;
      const next = offers[(offers.indexOf(offer) + (event.key === 'ArrowDown' ? 1 : -1) + offers.length) % offers.length];
      if (next) {
        this.#selectOffer(next);
        next.focus();
      }
    }
  };

  /** Sticky bar button: bring the bundle picker back into view and focus the selected offer. */
  #scrollToBundles() {
    const anchor = this.querySelector('[data-bundles]') ?? this.querySelector('.svp-offers');
    if (!anchor) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    anchor.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    this.#selectedOffer?.focus({ preventScroll: true });
  }

  /** @param {number} index */
  #showSlide(index) {
    const slides = this.#slides;
    if (!slides.length) return;
    this.#current = (index + slides.length) % slides.length;
    slides.forEach((s, i) => s.classList.toggle('is-on', i === this.#current));
    this.#thumbs.forEach((t, i) => t.classList.toggle('is-on', i === this.#current));
  }

  /** @param {HTMLElement} offer */
  #selectOffer(offer) {
    for (const o of this.#offers) {
      const on = o === offer;
      o.classList.toggle('is-sel', on);
      o.setAttribute('aria-checked', String(on));
      o.tabIndex = on ? 0 : -1;
    }
    this.#syncPrice();
    this.#syncGift();
  }

  /** Show the gift box only when the selected offer reaches its minimum quantity. */
  #syncGift() {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-gift-box]'));
    if (!box) return;
    const qty = Number(this.#selectedOffer?.dataset.qty || 1);
    box.hidden = qty < Number(box.dataset.giftMin || 1);
  }

  /** @param {HTMLButtonElement} color */
  #selectColor(color) {
    const row = color.closest('[data-unit]');
    if (!row) return;
    for (const c of row.querySelectorAll('.svp-color')) {
      c.classList.toggle('is-on', c === color);
      c.setAttribute('aria-checked', String(c === color));
    }
  }

  #syncPrice() {
    const total = this.#selectedOffer?.dataset.total ?? '';
    const label = this.dataset.atcLabel ?? '';
    const text = this.querySelector('[data-atc-text]');
    if (text) text.textContent = total ? `${label} — ${total}` : label;
    const sticky = this.querySelector('[data-sticky-price]');
    if (sticky) sticky.textContent = total;
  }

  /** @type {Record<string, { id: number, available: boolean }> | undefined} */
  #variantMap;

  /** @returns {Record<string, { id: number, available: boolean }>} */
  get #variants() {
    if (!this.#variantMap) {
      try {
        this.#variantMap = JSON.parse(this.querySelector('[data-variant-map]')?.textContent || '{}');
      } catch {
        this.#variantMap = {};
      }
    }
    return /** @type {Record<string, { id: number, available: boolean }>} */ (this.#variantMap);
  }

  /**
   * Variant ids to add, grouped: one entry per unit's colour (+ size) choice. Returns an error
   * message instead when a unit still needs a size.
   * @returns {{ items: { id: number, quantity: number }[], error?: string }}
   */
  #collectItems() {
    const offer = this.#selectedOffer;
    const qty = Number(offer?.dataset.qty || 1);
    const rows = offer ? Array.from(offer.querySelectorAll('[data-unit]')) : [];

    /** @type {Map<number, number>} */
    const counts = new Map();
    if (!rows.length) {
      counts.set(Number(this.dataset.defaultVariant), qty);
      return { items: Array.from(counts, ([id, quantity]) => ({ id, quantity })) };
    }

    for (const row of rows) {
      const color = /** @type {HTMLElement | null} */ (
        row.querySelector('.svp-color.is-on') ?? row.querySelector('.svp-color:not(:disabled)')
      )?.dataset.value;
      const select = /** @type {HTMLSelectElement | null} */ (row.querySelector('[data-size]'));
      if (select && !select.value) {
        select.classList.add('is-invalid');
        select.focus();
        const label = /** @type {HTMLElement} */ (row).dataset.unit ?? '';
        return { items: [], error: (this.dataset.sizeError || 'Please choose a size.').replace('[unit]', label) };
      }
      const key = select ? `${color}|${select.value}` : `${color}`;
      const variant = this.#variants[key];
      if (!variant?.available) {
        return { items: [], error: `${color}${select ? ' / ' + select.value : ''} — ${this.dataset.oosLabel || 'sold out'}` };
      }
      counts.set(variant.id, (counts.get(variant.id) ?? 0) + 1);
    }
    return { items: Array.from(counts, ([id, quantity]) => ({ id, quantity })) };
  }

  async #addToCart() {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-atc]'));
    if (!button || button.disabled || button.classList.contains('is-busy')) return;

    const errorEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    if (errorEl) errorEl.hidden = true;
    const { items, error } = this.#collectItems();
    if (error) {
      if (errorEl) {
        errorEl.textContent = error;
        errorEl.hidden = false;
      }
      return;
    }
    const giftId = Number(this.dataset.giftVariant || 0);
    button.classList.add('is-busy');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      if (giftId) {
        const current = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
        const hasGift = current.items?.some((/** @type {{ variant_id: number }} */ l) => l.variant_id === giftId);
        if (!hasGift) items.push({ id: giftId, quantity: 1 });
      }

      // Horizon's cart_add_url already ends in .js
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
        }),
      }).then((r) => r.json());

      if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'sv-product',
          itemCount: items.reduce((n, i) => n + i.quantity, 0),
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });

      // The drawer opens itself on this event when "auto open cart drawer" is on (settings_data).
      // Without a drawer (cart type = page), go to the cart page.
      if (!document.querySelector('cart-drawer-component')) window.location.href = Theme.routes.cart_url;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
    } finally {
      button.classList.remove('is-busy');
    }
  }

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** Show the sticky bar once the main button has scrolled above the viewport. */
  #observeAtc() {
    const atc = this.querySelector('[data-atc]');
    const sticky = /** @type {HTMLElement | null} */ (this.querySelector('[data-sticky]'));
    if (!atc || !sticky) return;
    this.#observer = new IntersectionObserver(([entry]) => {
      const show = !!entry && !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-shown', show);
      sticky.setAttribute('aria-hidden', String(!show));
      sticky.querySelector('[data-sticky-atc]')?.setAttribute('tabindex', show ? '0' : '-1');
    });
    this.#observer.observe(atc);
  }
}

if (!customElements.get('sv-product')) customElements.define('sv-product', SvProduct);
